/**
 * <cw-account>: Account for a signed-in customer (snippets/cw-account.liquid,
 * design-handoff/components/sign-in-account.md "Account").
 *
 * - Phones and tablets: a screen over the page and under the tabs (non-modal: the tabs stay
 *   live, and the Account tab shows as the one you are on); 1024+: a 420 panel.
 * - "Saved addresses" opens the address sheet; its line says how many and which is chosen.
 * - Orders on their way are cards above the list (v13 acc rows: state icon, "Arriving today
 *   before 3pm", "#1042 · 28 Sept · 3 items · ₹263", the stage as a tag), each opening its
 *   order page; the list below keeps the rest (assets/cw-tracking.js).
 * - EDIT (and "Add email for receipts") opens Edit profile: name, email, and the sign-in
 *   number shown but not changed. SAVE goes through Flow (cw-book-sync.js saveProfile), then
 *   the card shows the new words and a toast says "Profile updated".
 * Signed out, 'cw:account:open' never reaches here: cw-signin.js asks for sign-in first.
 */
import { CwSheet, screenUp, screenDown } from '@cw/sheet';
import { book } from '@cw/delivery';
import { activeOrders, iconHtml, esc } from '@cw/tracking';
import { saveProfile } from '@cw/book-sync';

const WIDE = window.matchMedia('(min-width: 1024px)');

/** "+919845012345" → "+91 98450 12345". */
function phoneText(raw) {
  const d = String(raw || '').replace(/\D/g, '');
  const ten = d.length === 12 && d.startsWith('91') ? d.slice(2) : d.length === 10 ? d : '';
  return ten ? `+91 ${ten.slice(0, 5)} ${ten.slice(5)}` : String(raw || '');
}

class CwAccount extends CwSheet {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('cw:account:open', this.#onRequest);
    document.addEventListener('cw:address:change', this.#paintAddresses);
    this.addEventListener('click', this.#onClick);
    this.addEventListener('submit', this.#onSubmit);
    const phone = this.querySelector('[data-cw="phone"]');
    if (phone) phone.textContent = phoneText(phone.dataset.raw);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('cw:account:open', this.#onRequest);
    document.removeEventListener('cw:address:change', this.#paintAddresses);
  }

  modal() {
    return WIDE.matches;
  }

  /** A screen, not a sheet: no swipe-down to close (the tabs take you elsewhere). */
  swipeable() {
    return false;
  }

  #onRequest = (event) => {
    event.preventDefault();
    this.#paintAddresses();
    this.#paintOrders();
    this.open(event.detail?.source);
    // Phones and tablets: a screen under the tabs, with the Account tab lit.
    if (!WIDE.matches) screenUp(this, 'account');
  };

  onClosed() {
    screenDown(this);
  }

  /** The orders on their way, as cards; the list keeps the rest. */
  #paintOrders() {
    const live = this.querySelector('[data-cw="live"]');
    if (!live) return;
    const list = activeOrders();
    live.hidden = list.length === 0;
    live.innerHTML = list
      .map(
        (o) => `<a class="cw-acct__liverow" href="${esc(o.url)}">
          <span class="cw-acct__liveicon">${iconHtml(o.icon)}</span>
          <span class="cw-acct__livetext"><span class="cw-acct__livehead">${esc(o.head)}</span><span class="cw-acct__livemeta">${esc(o.meta)}</span></span>
          <span class="cw-acct__livetag cw-acct__livetag--${o.tone}">${esc(o.tag)}</span>
        </a>`
      )
      .join('');
    const shown = new Set(list.map((o) => o.name));
    const past = this.querySelector('[data-cw="past"]');
    const rows = [...(past?.querySelectorAll('.cw-acct__order') || [])];
    for (const row of rows) row.hidden = shown.has(row.dataset.name);
    if (past) past.hidden = rows.length > 0 && rows.every((row) => row.hidden);
  }

  #paintAddresses = () => {
    const sub = this.querySelector('[data-cw="addr-sub"]');
    if (!sub) return;
    const b = book();
    const here = b.saved.find((a) => a.id === b.chosenId);
    sub.textContent = b.saved.length
      ? `${b.saved.length} saved${here ? ` · delivering to ${here.tag || here.label || 'your address'}` : ''}`
      : 'Add where we deliver to you';
  };

  /** Esc closes Edit profile first, then Account (CwSheet asks before closing). */
  onEscape() {
    if (this.#prof()?.hidden === false) {
      this.#closeProfile();
      return true;
    }
    return false;
  }

  #prof() {
    return this.querySelector('[data-cw="prof"]');
  }

  #openProfile(from) {
    const prof = this.#prof();
    if (!prof) return;
    this.profFrom = from;
    const form = prof.querySelector('form');
    form.elements.name.value = this.querySelector('[data-cw="name"]')?.textContent.trim() || '';
    const email = this.querySelector('[data-cw="email"]');
    form.elements.email.value = email && !email.hasAttribute('data-edit') ? email.textContent.trim() : '';
    const phone = prof.querySelector('[data-cw="prof-phone"]');
    if (phone) phone.textContent = phoneText(phone.textContent.trim());
    prof.querySelector('[data-cw="prof-err"]').hidden = true;
    prof.hidden = false;
    // After the tap's own focus has settled on EDIT.
    setTimeout(() => form.elements.name.focus({ preventScroll: true }), 60);
  }

  #closeProfile() {
    const prof = this.#prof();
    if (!prof) return;
    prof.hidden = true;
    this.profFrom?.focus?.({ preventScroll: true });
  }

  #onSubmit = async (event) => {
    if (!event.target.matches?.('[data-cw="prof-form"]')) return;
    event.preventDefault();
    const form = event.target;
    const err = form.querySelector('[data-cw="prof-err"]');
    const name = form.elements.name.value.trim();
    const email = form.elements.email.value.trim();
    const fail = (text) => {
      err.textContent = text;
      err.hidden = false;
    };
    if (!name) return fail('Your name, please.');
    if (email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) return fail('That email does not look right.');
    const save = form.querySelector('[data-cw="prof-save"]');
    save.disabled = true;
    const answer = await saveProfile(name, email);
    save.disabled = false;
    if (!answer || answer.error) {
      return fail(answer?.status === 400 && answer.error ? `${answer.error}.` : 'We could not save that just now. Please try again.');
    }
    const full = [answer.first_name, answer.last_name].filter(Boolean).join(' ') || name;
    const nameEl = this.querySelector('[data-cw="name"]');
    if (nameEl) nameEl.textContent = full;
    const initial = this.querySelector('[data-cw="initial"]');
    if (initial) initial.textContent = full.charAt(0).toUpperCase();
    const emailEl = this.querySelector('[data-cw="email"]');
    if (emailEl && answer.email) {
      const span = document.createElement('span');
      span.className = 'cw-acct__meta cw-acct__clamp';
      span.dataset.cw = 'email';
      span.textContent = answer.email;
      emailEl.replaceWith(span);
    }
    document.body.dataset.customerName = full;
    if (answer.email) document.body.dataset.customerEmail = answer.email;
    this.#closeProfile();
    document.dispatchEvent(new CustomEvent('cw:toast', { detail: { text: 'Profile updated' } }));
  };

  #onClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const edit = target.closest('[data-cw="edit"], [data-edit]');
    if (edit) return this.#openProfile(edit);
    if (target.closest('[data-cw="prof-close"]')) return this.#closeProfile();
    const addresses = target.closest('[data-cw="addresses"]');
    if (addresses) {
      document.dispatchEvent(new CustomEvent('cw:address:open', { cancelable: true, detail: { source: addresses } }));
    }
  };
}

if (!customElements.get('cw-account')) customElements.define('cw-account', CwAccount);
